import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CircleUser, KeyRound, LogOut, Moon, Sun } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { applyTheme, resolveTheme, type Theme } from '../lib/theme';
import { fetchSession } from '../lib/session';

type Props = {
  theme?: Theme;
  onThemeChange?: (theme: Theme) => void;
};

export default function ProfileMenu({ theme: themeProp, onThemeChange }: Props) {
  const [email, setEmail] = useState<string | null>(null);
  const [internalTheme, setInternalTheme] = useState<Theme>(() => resolveTheme());
  const theme = themeProp ?? internalTheme;
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const session = await fetchSession();
      if (session) setEmail(session.email);
    })();
  }, []);

  function changeTheme(value: string) {
    if (value !== 'light' && value !== 'dark') return;
    const next = value as Theme;
    applyTheme(next);
    setInternalTheme(next);
    onThemeChange?.(next);
  }

  async function onLogout() {
    try {
      await fetch('/auth/logout', { method: 'POST' });
    } finally {
      navigate('/login', { replace: true });
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-10 w-10 flex-none rounded-full"
          aria-label="Account menu"
        >
          <CircleUser className="!size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="min-w-0">
          <span className="block text-xs font-normal text-muted-foreground">Signed in as</span>
          <span className="block truncate text-sm font-semibold">{email || 'Account'}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={changeTheme}>
          <DropdownMenuRadioItem value="light">
            <Sun />
            Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon />
            Dark
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <KeyRound />
            Profile / API token
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => { void onLogout(); }}
          className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
        >
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
